import { FileText, Image, File, FileSpreadsheet, FileVideo, FileAudio, FileArchive, FileCode } from 'lucide-react';

export const getFileIcon = (filename: string) => {
  if (!filename)
    return (
      <div className="flex items-center justify-center w-full h-full bg-gray-100 dark:bg-gray-700 rounded">
        <File className="h-8 w-8 text-gray-400" />
      </div>
    );

  const extension = filename.toLowerCase().split('.').pop();
  switch (extension) {
    case 'pdf':
      return (
        <div className="flex items-center justify-center w-full h-full bg-red-100 dark:bg-red-900/20 rounded">
          <FileText className="h-8 w-8 text-red-600" />
        </div>
      );
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'svg':
    case 'webp':
      return (
        <div className="flex items-center justify-center w-full h-full bg-green-100 dark:bg-green-900/20 rounded">
          <Image className="h-8 w-8 text-green-600" />
        </div>
      );
    case 'doc':
    case 'docx':
    case 'txt':
    case 'rtf':
      return (
        <div className="flex items-center justify-center w-full h-full bg-blue-100 dark:bg-blue-900/20 rounded">
          <FileText className="h-8 w-8 text-blue-600" />
        </div>
      );
    case 'xls':
    case 'xlsx':
    case 'csv':
      return (
        <div className="flex items-center justify-center w-full h-full bg-yellow-100 dark:bg-yellow-900/20 rounded">
          <FileSpreadsheet className="h-8 w-8 text-yellow-600" />
        </div>
      );
    case 'mp4':
    case 'avi':
    case 'mov':
    case 'wmv':
    case 'flv':
    case 'webm':
      return (
        <div className="flex items-center justify-center w-full h-full bg-purple-100 dark:bg-purple-900/20 rounded">
          <FileVideo className="h-8 w-8 text-purple-600" />
        </div>
      );
    case 'mp3':
    case 'wav':
    case 'flac':
    case 'aac':
    case 'ogg':
      return (
        <div className="flex items-center justify-center w-full h-full bg-indigo-100 dark:bg-indigo-900/20 rounded">
          <FileAudio className="h-8 w-8 text-indigo-600" />
        </div>
      );
    case 'zip':
    case 'rar':
    case '7z':
    case 'tar':
    case 'gz':
      return (
        <div className="flex items-center justify-center w-full h-full bg-orange-100 dark:bg-orange-900/20 rounded">
          <FileArchive className="h-8 w-8 text-orange-600" />
        </div>
      );
    case 'js':
    case 'ts':
    case 'jsx':
    case 'tsx':
    case 'html':
    case 'css':
    case 'json':
    case 'xml':
    case 'py':
    case 'java':
    case 'cpp':
    case 'c':
      return (
        <div className="flex items-center justify-center w-full h-full bg-teal-100 dark:bg-teal-900/20 rounded">
          <FileCode className="h-8 w-8 text-teal-600" />
        </div>
      );
    default:
      return (
        <div className="flex items-center justify-center w-full h-full bg-gray-100 dark:bg-gray-700 rounded">
          <File className="h-8 w-8 text-gray-500" />
        </div>
      );
  }
};

export const getFileType = (filename: string): string => {
  if (!filename) return 'Unknown';
  const extension = filename.toLowerCase().split('.').pop();
  switch (extension) {
    case 'pdf':
      return 'PDF';
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'svg':
    case 'webp':
      return 'Image';
    case 'doc':
    case 'docx':
    case 'txt':
    case 'rtf':
      return 'Document';
    case 'xls':
    case 'xlsx':
    case 'csv':
      return 'Spreadsheet';
    case 'mp4':
    case 'avi':
    case 'mov':
    case 'wmv':
    case 'flv':
    case 'webm':
      return 'Video';
    case 'mp3':
    case 'wav':
    case 'flac':
    case 'aac':
    case 'ogg':
      return 'Audio';
    case 'zip':
    case 'rar':
    case '7z':
    case 'tar':
    case 'gz':
      return 'Archive';
    case 'js':
    case 'ts':
    case 'jsx':
    case 'tsx':
    case 'html':
    case 'css':
    case 'json':
    case 'xml':
    case 'py':
    case 'java':
    case 'cpp':
    case 'c':
      return 'Code';
    default:
      return 'File';
  }
};

export const getTypeColor = (type: string): string => {
  switch (type) {
    case 'PDF':
      return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
    case 'Image':
      return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
    case 'Document':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
    case 'Spreadsheet':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
    case 'Video':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400';
    case 'Audio':
      return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-400';
    case 'Archive':
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400';
    case 'Code':
      return 'bg-teal-100 text-teal-800 dark:bg-teal-900/20 dark:text-teal-400';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  }
};

export const isImageFile = (filename: string): boolean => {
  if (!filename) return false;
  const extension = filename.toLowerCase().split('.').pop();
  return ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(extension || '');
};
